import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Archive, CheckCircle, Loader2, MailCheck, RefreshCw, Search } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { apiFetch } from '../lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const statusStyle = {
  accepted: 'bg-[#E1F1EE] text-[#176C62]',
  partially_accepted: 'bg-[#FBF1DC] text-[#8A6D3B]',
  failed: 'bg-[#FCEEEA] text-[#A3503B]',
};

const sourceLabel = {
  compose: 'New email',
  campaign: 'Campaign',
  scheduled_reply: 'Scheduled reply',
  scheduled_compose: 'Scheduled email',
};

function localDate(value) {
  if (!value) return 'Not accepted';
  return new Date(value).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
}

export default function DeliveryHistory() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiFetch(`${BACKEND_URL}/api/delivery-events`);
      const data = await response.json();
      setEvents(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return events;
    return events.filter(event => [
      event.subject,
      event.sender_email,
      ...(event.recipients || []),
      event.message_id,
    ].some(value => String(value || '').toLowerCase().includes(term)));
  }, [events, query]);

  const acceptedCount = events.filter(event => event.status === 'accepted').length;
  const failedCount = events.filter(event => event.status === 'failed').length;

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6" data-testid="delivery-history-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1.5">
            <MailCheck className="w-7 h-7 text-primary" />
            <h1 className="text-2xl sm:text-3xl font-serif font-medium text-foreground">Sent activity</h1>
          </div>
          <p className="text-sm text-muted-foreground" data-testid="delivery-history-explanation">
            SMTP acceptance confirms the mail server took responsibility for delivery. It does not confirm an open.
          </p>
        </div>
        <Button variant="outline" className="rounded-full" onClick={loadEvents} disabled={loading} data-testid="refresh-delivery-history-button">
          {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-5 max-w-md">
        <div className="border border-border bg-card rounded-lg p-4" data-testid="accepted-delivery-count">
          <p className="text-2xl font-serif text-[#176C62]">{acceptedCount}</p>
          <p className="text-xs text-muted-foreground">SMTP accepted</p>
        </div>
        <div className="border border-border bg-card rounded-lg p-4" data-testid="failed-delivery-count">
          <p className="text-2xl font-serif text-[#A3503B]">{failedCount}</p>
          <p className="text-xs text-muted-foreground">Failed attempts</p>
        </div>
      </div>

      <div className="relative max-w-xl mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input value={query} onChange={event => setQuery(event.target.value)} placeholder="Search recipient, subject or Message-ID" className="pl-9" data-testid="delivery-history-search-input" />
      </div>

      {loading ? (
        <div className="py-16 flex justify-center" data-testid="delivery-history-loading"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center border-y border-border" data-testid="delivery-history-empty-state">
          <MailCheck className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
          <p className="font-serif text-lg text-foreground">No matching delivery receipts</p>
          <p className="text-sm text-muted-foreground mt-1">New sends will appear here after the SMTP server responds.</p>
        </div>
      ) : (
        <div className="divide-y divide-border border-y border-border" data-testid="delivery-history-list">
          {filtered.map(event => {
            const Icon = event.status === 'failed' ? AlertTriangle : CheckCircle;
            return (
              <article className="py-5 grid gap-3 md:grid-cols-[1fr_auto]" key={event.id} data-testid={`delivery-event-${event.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1.5">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${statusStyle[event.status] || statusStyle.failed}`} data-testid={`delivery-status-${event.id}`}>
                      <Icon className="w-3 h-3" /> {event.status === 'accepted' ? 'SMTP accepted' : event.status.replace('_', ' ')}
                    </span>
                    <span className="text-xs text-muted-foreground">{sourceLabel[event.source_type] || event.source_type}</span>
                  </div>
                  <h2 className="text-base font-semibold text-foreground break-words">{event.subject || '(no subject)'}</h2>
                  <p className="text-sm text-muted-foreground break-all" data-testid={`delivery-recipients-${event.id}`}>To: {(event.recipients || []).join(', ')}</p>
                  <p className="text-xs text-muted-foreground mt-1 break-all">From: {event.sender_email}</p>
                  {event.smtp_error && <p className="text-xs text-[#A3503B] mt-2" data-testid={`delivery-error-${event.id}`}>{event.smtp_error}</p>}
                </div>
                <div className="md:text-right flex flex-col md:items-end gap-1.5">
                  <time className="text-sm font-semibold text-foreground" data-testid={`delivery-time-${event.id}`}>{localDate(event.smtp_accepted_at)}</time>
                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" data-testid={`sent-folder-status-${event.id}`}>
                    <Archive className="w-3 h-3" /> {event.sent_folder_status === 'archived' ? `Saved in ${event.sent_folder}` : 'Sent-folder copy unavailable'}
                  </span>
                  <span className="text-[11px] text-muted-foreground break-all max-w-xs" data-testid={`delivery-message-id-${event.id}`}>{event.message_id}</span>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </main>
  );
}